import * as React from 'react';
import { Link } from 'react-router-dom';
import { Network } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { VISUAL } from '../../../design-system/visual';
import { useApi } from '../../../design-system/useApi';
import type { OrgChartNode } from '../types';

/** Build a manager_id -> children[] map, plus the list of roots (no manager,
 *  or a manager_id that doesn't resolve — e.g. was cross-tenant or deleted). */
function buildTree(nodes: OrgChartNode[]) {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const children = new Map<string, OrgChartNode[]>();
  const roots: OrgChartNode[] = [];
  for (const n of nodes) {
    if (n.manager_id && byId.has(n.manager_id)) {
      const list = children.get(n.manager_id) ?? [];
      list.push(n);
      children.set(n.manager_id, list);
    } else {
      roots.push(n);
    }
  }
  return { children, roots };
}

export function OrgChartPage() {
  const { data, loading, error, reload } = useApi<{ nodes: OrgChartNode[] }>('/api/hr/org-chart');
  const nodes = data?.nodes ?? [];
  const { children, roots } = React.useMemo(() => buildTree(nodes), [nodes]);

  return (
    <div>
      <PageHeader
        eyebrow="Team / Org chart"
        title="Org chart"
        description="Who reports to whom, based on each employee's manager assignment."
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-3xl">
        {loading ? (
          <LoadingState variant="page" />
        ) : error ? (
          <ErrorState description={error} onRetry={reload} />
        ) : nodes.length === 0 ? (
          <EmptyState
            icon={<Network className="h-6 w-6" strokeWidth={1.6} />}
            title="No employees yet"
            description="Invite your team from People, then assign managers to build the chart."
          />
        ) : (
          <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
            <ul className="space-y-1">
              {roots.map((n) => (
                <OrgChartRow key={n.id} node={n} depth={0} childrenMap={children} />
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}

function OrgChartRow({
  node, depth, childrenMap,
}: { node: OrgChartNode; depth: number; childrenMap: Map<string, OrgChartNode[]> }) {
  const kids = childrenMap.get(node.id) ?? [];
  return (
    <li>
      <Link
        to={`/dashboard/hr/team/${node.id}`}
        className="flex items-center gap-2.5 py-2 px-2.5 rounded-lg hover:bg-muted/50 transition-colors"
        style={{ paddingLeft: `${depth * 24 + 10}px` }}
      >
        <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${node.is_active ? 'bg-success' : 'bg-muted-foreground/40'}`} />
        <span className="text-[13.5px] font-medium text-foreground">{node.name}</span>
        {node.designation && <span className="text-[12px] text-muted-foreground">· {node.designation}</span>}
        {node.department && <span className="text-[11.5px] text-muted-foreground/70">({node.department})</span>}
      </Link>
      {kids.length > 0 && (
        <ul className="space-y-1">
          {kids.map((k) => (
            <OrgChartRow key={k.id} node={k} depth={depth + 1} childrenMap={childrenMap} />
          ))}
        </ul>
      )}
    </li>
  );
}
